import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { deliveryClient } from '../../../lib/connect/clients';
// ============================================================================
// ProgramDocument — Create / List / Delete. fileId is a cross-schema FK
// to artifacts.artifacts (same constraint as TaskAttachment).
// ============================================================================
// Exported so other surfaces filtering the same entity (e.g. the company
// staffing drawer's companyAssignmentId-scoped list) invalidate together with
// these mutations instead of drifting onto a second, unrelated key.
export const DOC_KEY = 'program document';
export function useProgramDocuments(programId) {
    return useQuery({
        queryKey: [
            DOC_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listProgramDocuments({
                programId,
                category: '',
                companyAssignmentId: ''
            });
            return {
                items: resp.documents
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
/** One entity's slice of the program documents: scoped to a company
 *  assignment, a contact, or an issue. Shares the DOC_KEY prefix so every
 *  document mutation invalidates these lists too. */ export function useScopedProgramDocuments(programId, scope) {
    const companyAssignmentId = scope.companyAssignmentId ?? '';
    const contactId = scope.contactId ?? '';
    const issueId = scope.issueId ?? '';
    return useQuery({
        queryKey: [
            DOC_KEY,
            'list',
            programId,
            companyAssignmentId,
            contactId,
            issueId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listProgramDocuments({
                programId,
                category: '',
                companyAssignmentId,
                contactId,
                issueId
            });
            return resp.documents;
        },
        enabled: !!programId && (!!companyAssignmentId || !!contactId || !!issueId),
        staleTime: 30000
    });
}
export function useCreateProgramDocument() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await deliveryClient.createProgramDocument({
                programId: input.programId,
                title: input.title,
                description: input.description,
                docDate: Timestamp.fromDate(input.docDate),
                fileId: input.fileId,
                fileName: input.fileName,
                category: input.category,
                uploadedBy: input.uploadedBy,
                companyAssignmentId: input.companyAssignmentId ?? '',
                contactId: input.contactId ?? '',
                issueId: input.issueId ?? ''
            });
            return resp.document;
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    DOC_KEY
                ]
            });
            toast.success('Document recorded');
        },
        onError: (err)=>{
            toast.error(`Failed to record document: ${err.message}`);
        }
    });
}
export function useDeleteProgramDocument() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id)=>{
            await deliveryClient.deleteProgramDocument({
                id
            });
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    DOC_KEY
                ]
            });
            toast.success('Document deleted');
        },
        onError: (err)=>{
            toast.error(`Failed to delete document: ${err.message}`);
        }
    });
}
// ============================================================================
// ProgramModification — List only (populated upstream via syncFromExtraction)
// ============================================================================
const MOD_KEY = 'program modification';
export function useProgramModifications(programId) {
    return useQuery({
        queryKey: [
            MOD_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listProgramModifications({
                programId
            });
            return {
                items: resp.modifications
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
// ============================================================================
// AuditTemplate — Upsert / List. Company-scoped (companyId = tenant_id).
// Templates carry their AuditTemplateQuestions inline as a nested array.
// ============================================================================
const TEMPLATE_KEY = 'audit template';
export function useAuditTemplates(companyId) {
    return useQuery({
        queryKey: [
            TEMPLATE_KEY,
            'list',
            companyId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listAuditTemplates({
                companyId,
                programId: ''
            });
            return {
                items: resp.templates
            };
        },
        enabled: !!companyId,
        staleTime: 30000
    });
}
export function useUpsertAuditTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await deliveryClient.upsertAuditTemplate({
                id: input.id,
                companyId: input.companyId,
                name: input.name,
                area: input.area,
                isDefault: input.isDefault,
                // Nested AuditTemplateQuestions — pangaea handles the diff
                // (upserts new rows, prunes removed ones).
                questions: input.questions.map((q)=>({
                        id: q.id,
                        templateId: input.id,
                        question: q.question,
                        sortOrder: q.sortOrder
                    })),
                programId: ''
            });
            return resp.template;
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    TEMPLATE_KEY
                ]
            });
            toast.success('Template saved');
        },
        onError: (err)=>{
            toast.error(`Failed to save template: ${err.message}`);
        }
    });
}
